import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../../api/client';
import { useAuth } from '../../providers/AuthProvider';
import { StatusMessage } from '../../components/ui/Feedback';
import { homeFor } from '../../lib/roles';

/**
 * Magic-link landing (`/auth/magic?token=…`). Consumes the single-use token, establishes the
 * session, and routes to the right shell. The token is consumed EXACTLY ONCE — a `ref` guards
 * against React StrictMode's double effect invocation (a second consume would fail and surface a
 * spurious error on an otherwise-successful sign-in). On failure it offers the way back to login.
 */
export function MagicLink() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setCsrfToken, refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const token = params.get('token');
    if (!token) {
      setError('This sign-in link is missing its token.');
      return;
    }
    void (async () => {
      try {
        const res = await api.auth.verifyMagicLink({ token });
        setCsrfToken(res.csrfToken);
        await refresh();
        navigate(homeFor(res.principal.role), { replace: true });
      } catch (err) {
        setError(errorMessage(err));
      }
    })();
  }, [params, navigate, refresh, setCsrfToken]);

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <header>
          <h1>Signing you in…</h1>
        </header>
        <main id="main-content">
          {error ? (
            <div className="stack">
              <StatusMessage tone="err">{error}</StatusMessage>
              <p>
                Your link may have expired or already been used.{' '}
                <a href="/login">Back to sign in</a> to request a new one.
              </p>
            </div>
          ) : (
            <p className="status" role="status" aria-live="polite">
              One moment while we verify your link…
            </p>
          )}
        </main>
      </div>
    </div>
  );
}
